import React, { useState, useEffect } from 'react';
import { 
    Table, 
    Button, 
    Input, 
    Space, 
    Modal, 
    Form, 
    message, 
    Popconfirm,
    Tag,
    Select,
    Tooltip,
    Dropdown,
    Card,
    Collapse,
    Row,
    Col
} from 'antd';
import { 
    SearchOutlined, 
    PlusOutlined, 
    EditOutlined, 
    DeleteOutlined,
    EyeOutlined,
    UserOutlined,
    MailOutlined,
    PhoneOutlined,
    LockOutlined,
    MoreOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ClockCircleOutlined,
    FilterOutlined,
    ReloadOutlined,
    SafetyCertificateOutlined,
    SafetyOutlined
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import adminService from '../../../services/adminService';
import { setOriginalAdminUser, loginSuccess, loginStart } from '../../../redux/userSlice';
import { useSelector } from 'react-redux';
import DashboardLayout from '../../../components/Dashboard/DashboardLayout';
import PageHeader from '../../../components/Common/PageHeader';
import UserBadges from '../../../components/Common/UserBadges';
import useColumnVisibility from '../../../components/Common/ColumnVisibilityControl';
import './Users.css';

const { Option } = Select;
const { Panel } = Collapse;

const Users = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [tablePagination, setTablePagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const [modalVisible, setModalVisible] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [searchParams, setSearchParams] = useState({});
    const [showPasswordField, setShowPasswordField] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();
    const [searchForm] = Form.useForm();
    const dispatch = useDispatch();
    const { currentUser } = useSelector((state) => state.user);

    const fetchUsers = async (params = {}) => {
        try {
            setLoading(true);
            const response = await adminService.getUsers({
                page: params.page ?? tablePagination.current,
                per_page: params.per_page ?? tablePagination.pageSize,
                ...params
            });

            // Support multiple backend response shapes:
            // - Laravel paginator: { data: [...], total, per_page, current_page }
            // - Resource w/ meta: { data: [...], meta: { total, per_page, current_page } }
            // - Plain array
            const list =
                Array.isArray(response) ? response :
                Array.isArray(response?.data?.data) ? response.data.data :
                Array.isArray(response?.data) ? response.data :
                Array.isArray(response?.users) ? response.users :
                [];

            const total =
                (typeof response?.total === 'number' ? response.total : null) ??
                (typeof response?.meta?.total === 'number' ? response.meta.total : null) ??
                (typeof response?.pagination?.total === 'number' ? response.pagination.total : null) ??
                list.length;

            const current =
                (typeof response?.current_page === 'number' ? response.current_page : null) ??
                (typeof response?.meta?.current_page === 'number' ? response.meta.current_page : null) ??
                (typeof params?.page === 'number' ? params.page : null) ??
                1;

            const pageSize =
                (typeof response?.per_page === 'number' ? response.per_page : null) ??
                (typeof response?.meta?.per_page === 'number' ? response.meta.per_page : null) ??
                (typeof params?.per_page === 'number' ? params.per_page : null) ??
                tablePagination.pageSize;

            setUsers(list);
            setTablePagination(prev => ({
                ...prev,
                current,
                pageSize,
                total
            }));
        } catch (error) {
            message.error('Failed to fetch users');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleSearch = (values) => {
        // Build search string from all fields
        const searchFields = [];
        if (values.first_name) searchFields.push(values.first_name);
        if (values.last_name) searchFields.push(values.last_name);
        if (values.email) searchFields.push(values.email);
        if (values.phone_number) searchFields.push(values.phone_number);
        
        const search = searchFields.join(' ');
        const params = { search };
        
        // Add approval status filter if selected
        if (values.approval_status) {
            params.approval_status = values.approval_status;
        }
        
        setSearchParams(params);
        setTablePagination(prev => ({ ...prev, current: 1 }));
        fetchUsers({ ...params, page: 1 });
    };

    const handleReset = () => {
        searchForm.resetFields();
        setSearchParams({});
        setTablePagination(prev => ({ ...prev, current: 1 }));
        fetchUsers({ page: 1 });
    };

    const handleCreateUser = () => {
        setEditingUser(null);
        form.resetFields();
        setModalVisible(true);
    };

    const handleEditUser = (user) => {
        setEditingUser(user);
        setShowPasswordField(false);
        form.resetFields();
        form.setFieldsValue({
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
            roles: user.roles
        });
        setModalVisible(true);
    };

    const handleDeleteUser = async (userId) => {
        try {
            await adminService.deleteUser(userId);
            message.success('User deleted successfully');
            fetchUsers(searchParams);
        } catch (error) {
            message.error('Failed to delete user');
        }
    };

    const handleViewAsUser = async (userId) => {
        try {
            setLoading(true);
            dispatch(loginStart());
            const response = await adminService.viewAsUser({
                user_id: userId
            });
            console.log("test: ", response);
            // Store the original admin user in Redux
            dispatch(setOriginalAdminUser({
                user: currentUser,
                token: localStorage.getItem('token')
            }));            
            // Update the current user in Redux
            dispatch(loginSuccess(response));

            
            message.success('Now viewing as user');
            window.location.href = '/dashboard';
        } catch (error) {
            message.error('Failed to view as user');
        } finally {
            setLoading(false);
        }
    };

    const handleApproveUser = async (userId) => {
        try {
            await adminService.approveUser(userId);
            message.success('User approved successfully');
            fetchUsers(searchParams);
        } catch (error) {
            message.error('Failed to approve user');
        }
    };

    const handleRejectUser = async (userId, userStatus = 'pending') => {
        const isDeactivation = userStatus === 'approved';
        const title = isDeactivation ? 'Deactivate User Account' : 'Reject User Account';
        const actionText = isDeactivation ? 'Deactivate' : 'Reject';
        
        Modal.confirm({
            title: title,
            content: (
                <div>
                    <p>Please provide a reason for {isDeactivation ? 'deactivating' : 'rejecting'} this user account:</p>
                    <Input.TextArea 
                        id="rejection-reason"
                        placeholder={`Enter ${isDeactivation ? 'deactivation' : 'rejection'} reason...`}
                        rows={3}
                        maxLength={500}
                    />
                </div>
            ),
            okText: actionText,
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                const reason = document.getElementById('rejection-reason').value;
                if (!reason.trim()) {
                    message.error(`Please provide a ${isDeactivation ? 'deactivation' : 'rejection'} reason`);
                    return;
                }
                try {
                    await adminService.rejectUser(userId, { rejection_reason: reason });
                    message.success(`User ${isDeactivation ? 'deactivated' : 'rejected'} successfully`);
                    fetchUsers(searchParams);
                } catch (error) {
                    message.error(`Failed to ${isDeactivation ? 'deactivate' : 'reject'} user`);
                }
            }
        });
    };

    const handleToggleVerifiedBadge = async (userId) => {
        try {
            await adminService.toggleVerifiedBadge(userId);
            message.success('Verified badge toggled successfully');
            fetchUsers(searchParams);
        } catch (error) {
            message.error('Failed to toggle verified badge');
        }
    };

    const handleToggleSellerTrustBadge = async (userId) => {
        try {
            await adminService.toggleSellerTrustBadge(userId);
            message.success('Seller Trust Badge toggled successfully');
            fetchUsers(searchParams);
        } catch (error) {
            message.error('Failed to toggle seller trust badge');
        }
    };

    const handleToggleBuyerProtectionPlus = async (userId) => {
        try {
            await adminService.toggleBuyerProtectionPlus(userId);
            message.success('Buyer Protection Plus toggled successfully');
            fetchUsers(searchParams);
        } catch (error) {
            message.error('Failed to toggle buyer protection plus');
        }
    };

    const handleModalSubmit = async (values) => {
        try {
            setSubmitting(true);
            if (editingUser && !showPasswordField) {
                delete values.password;
                delete values.password_confirmation;
            }
            
            if (editingUser) {
                await adminService.updateUser(editingUser.id, values);
                message.success('User updated successfully');
            } else {
                await adminService.createUser(values);
                message.success('User created successfully');
            }
            setModalVisible(false);
            fetchUsers(searchParams);
        } catch (error) {
            if (error.response?.data?.errors) {
                const errors = error.response.data.errors;
                Object.keys(errors).forEach(key => {
                    message.error(`${key.charAt(0).toUpperCase() + key.slice(1)}: ${errors[key][0]}`);
                });
            } else if (error.response?.data?.message) {
                message.error(error.response.data.message);
            } else {
                message.error(editingUser ? 'Failed to update user' : 'Failed to create user');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const getActionItems = (record) => {
        const items = [
            {
                key: 'view',
                label: 'View as User',
                icon: <EyeOutlined />,
                onClick: () => handleViewAsUser(record.id)
            },
            {
                key: 'edit',
                label: 'Edit User',
                icon: <EditOutlined />,
                onClick: () => handleEditUser(record)
            }
        ];

        // Add approval/rejection actions based on status
        if (record.approval_status === 'pending') {
            items.push(
                {
                    key: 'approve',
                    label: 'Approve User',
                    icon: <CheckCircleOutlined />,
                    onClick: () => handleApproveUser(record.id)
                },
                {
                    key: 'reject',
                    label: 'Reject User',
                    icon: <CloseCircleOutlined />,
                    danger: true,
                    onClick: () => handleRejectUser(record.id, record.approval_status)
                }
            );
        } else if (record.approval_status === 'approved') {
            items.push({
                key: 'reject',
                label: 'Deactivate Account',
                icon: <CloseCircleOutlined />,
                danger: true,
                onClick: () => handleRejectUser(record.id, record.approval_status)
            });
        } else if (record.approval_status === 'rejected') {
            items.push({
                key: 'approve',
                label: 'Reactivate Account',
                icon: <CheckCircleOutlined />,
                onClick: () => handleApproveUser(record.id)
            });
        }

        // Add badge management actions
        items.push({
            type: 'divider'
        });

        items.push({
            key: 'toggle-verified',
            label: record.is_verified ? 'Remove Verified Badge' : 'Grant Verified Badge',
            icon: <CheckCircleOutlined style={{ color: record.is_verified ? '#ff4d4f' : '#1890ff' }} />,
            onClick: () => handleToggleVerifiedBadge(record.id)
        });

        items.push({
            key: 'toggle-seller-trust',
            label: record.seller_trust_badge ? 'Remove Seller Trust Badge' : 'Grant Seller Trust Badge',
            icon: <SafetyCertificateOutlined style={{ color: record.seller_trust_badge ? '#ff4d4f' : '#52c41a' }} />,
            onClick: () => handleToggleSellerTrustBadge(record.id)
        });

        items.push({
            key: 'toggle-buyer-protection',
            label: record.buyer_protection_plus ? 'Remove Buyer Protection Plus' : 'Grant Buyer Protection Plus',
            icon: <SafetyOutlined style={{ color: record.buyer_protection_plus ? '#ff4d4f' : '#faad14' }} />,
            onClick: () => handleToggleBuyerProtectionPlus(record.id)
        });

        items.push({
            type: 'divider'
        });

        items.push({
            key: 'delete',
            label: 'Delete User',
            icon: <DeleteOutlined />,
            danger: true,
            onClick: () => {
                Modal.confirm({
                    title: 'Are you sure you want to delete this user?',
                    content: 'This action cannot be undone.',
                    okText: 'Yes',
                    okType: 'danger',
                    cancelText: 'No',
                    onOk: () => handleDeleteUser(record.id)
                });
            }
        });

        return items;
    };

    const baseColumns = [
        {
            title: 'Name',
            key: 'name',
            render: (_, record) => (
                <Space>
                    <UserOutlined />
                    <span>{`${record.first_name} ${record.last_name}`}</span>
                    <UserBadges user={record} iconSize={14} />
                </Space>
            ),
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            render: (text) => (
                <Space>
                    <MailOutlined />
                    {text}
                </Space>
            ),
        },
        {
            title: 'Phone',
            dataIndex: 'phone_number',
            key: 'phone_number',
            render: (text) => (
                <Space>
                    <PhoneOutlined />
                    {text || 'N/A'}
                </Space>
            ),
        },
        {
            title: 'Approval Status',
            dataIndex: 'approval_status',
            key: 'approval_status',
            render: (status) => {
                const statusConfig = {
                    pending: { color: 'orange', icon: <ClockCircleOutlined />, text: 'Pending' },
                    approved: { color: 'green', icon: <CheckCircleOutlined />, text: 'Approved' },
                    rejected: { color: 'red', icon: <CloseCircleOutlined />, text: 'Rejected' }
                };
                const config = statusConfig[status] || statusConfig.pending;
                return (
                    <Tag color={config.color} icon={config.icon}>
                        {config.text}
                    </Tag>
                );
            },
        },
        {
            title: 'Rejection Reason',
            dataIndex: 'rejection_reason',
            key: 'rejection_reason',
            render: (reason, record) => {
                if (record.approval_status === 'rejected' && reason) {
                    return (
                        <Tooltip title={reason} placement="topLeft">
                            <div style={{ 
                                maxWidth: '200px', 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                color: '#ff4d4f'
                            }}>
                                {reason}
                            </div>
                        </Tooltip>
                    );
                }
                return <span style={{ color: '#999' }}>N/A</span>;
            },
        },
        {
            title: 'Roles',
            dataIndex: 'roles',
            key: 'roles',
            render: (roles) => (
                <>
                    {roles?.map(role => (
                        <Tag key={role} color={role === 'admin' ? 'red' : 'blue'}>
                            {role}
                        </Tag>
                    ))}
                </>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 100,
            fixed: 'right',
            render: (_, record) => (
                <Dropdown
                    menu={{
                        items: getActionItems(record)
                    }}
                    trigger={['click']}
                >
                    <Button 
                        icon={<MoreOutlined />}
                        type="text"
                    />
                </Dropdown>
            ),
        },
    ];

    // Column visibility control
    const { visibleColumns, ColumnVisibilityUI } = useColumnVisibility({
        baseColumns: baseColumns,
        storageKey: 'usersColumnsVisibility'
    });
    const filteredColumns = visibleColumns;

    return (
        <DashboardLayout>
            <PageHeader
                title="User Management"
                description="Manage your platform users"
                buttonText="Add User"
                buttonIcon={<PlusOutlined />}
                onButtonClick={handleCreateUser}
            />

            <Card style={{ marginBottom: 16 }}>
                <Collapse defaultActiveKey={['1']} ghost>
                    <Panel 
                        header={
                    <Space>
                                <FilterOutlined />
                                <span>Advanced Search</span>
                            </Space>
                        } 
                        key="1"
                    >
                        <Form
                            form={searchForm}
                            layout="vertical"
                            onFinish={handleSearch}
                        >
                            <Row gutter={16}>
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="first_name"
                                        label="First Name"
                                    >
                                        <Input 
                                            prefix={<UserOutlined />}
                                            placeholder="First Name" 
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="last_name"
                                        label="Last Name"
                                    >
                                        <Input 
                                            prefix={<UserOutlined />}
                                            placeholder="Last Name" 
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="email"
                                        label="Email"
                                    >
                                        <Input 
                                            prefix={<MailOutlined />}
                                            placeholder="Email Address" 
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="phone_number"
                                        label="Phone Number"
                                    >
                                        <Input 
                                            prefix={<PhoneOutlined />}
                                            placeholder="Phone Number" 
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="approval_status"
                                        label="Approval Status"
                                    >
                                        <Select 
                                            placeholder="Select Approval Status"
                                            allowClear
                        >
                            <Option value="pending">Pending</Option>
                            <Option value="approved">Approved</Option>
                            <Option value="rejected">Rejected</Option>
                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Form.Item>
                                <Space>
                    <Button
                        type="primary"
                                        htmlType="submit"
                                        icon={<SearchOutlined />}
                                    >
                                        Search
                                    </Button>
                                    <Button 
                                        onClick={handleReset}
                                        icon={<ReloadOutlined />}
                    >
                                        Reset
                    </Button>
                </Space>
                            </Form.Item>
                        </Form>
                    </Panel>
                </Collapse>
            </Card>

            {/* Column Visibility Control */}
            {ColumnVisibilityUI}

                <Table
                    scroll={{ x: 1000 }}
                    columns={filteredColumns}
                    dataSource={users}
                    loading={loading}
                    rowKey="id"
                    onChange={(pagination) => {
                        const next = {
                            current: pagination?.current ?? 1,
                            pageSize: pagination?.pageSize ?? tablePagination.pageSize
                        };
                        setTablePagination(prev => ({ ...prev, ...next }));
                        fetchUsers({
                            ...searchParams,
                            page: next.current,
                            per_page: next.pageSize
                        });
                    }}
                    pagination={{
                        current: tablePagination.current,
                        pageSize: tablePagination.pageSize,
                        total: tablePagination.total,
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} users`
                    }}
                />

            <Modal
                title={editingUser ? 'Edit User' : 'Create User'}
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    setShowPasswordField(false);
                    setSubmitting(false);
                }}
                footer={null}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleModalSubmit}
                >
                    <Form.Item
                        name="first_name"
                        label="First Name"
                        rules={[{ required: true, message: 'Please enter first name' }]}
                    >
                        <Input prefix={<UserOutlined />} />
                    </Form.Item>

                    <Form.Item
                        name="last_name"
                        label="Last Name"
                        rules={[{ required: true, message: 'Please enter last name' }]}
                    >
                        <Input prefix={<UserOutlined />} />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        label="Email"
                        rules={[
                            { required: true, message: 'Please enter email' },
                            { type: 'email', message: 'Please enter a valid email' }
                        ]}
                    >
                        <Input prefix={<MailOutlined />} />
                    </Form.Item>

                    {!editingUser ? (
                        <>
                            <Form.Item
                                name="roles"
                                label="Roles"
                                rules={[{ required: true, message: 'Please select roles' }]}
                            >
                                <Select mode="multiple" placeholder="Select roles">
                                    <Option value="user">User</Option>
                                    <Option value="admin">Admin</Option>
                                </Select>
                            </Form.Item>

                            <Form.Item
                                name="password"
                                label="Password"
                                rules={[
                                    { required: true, message: 'Please enter password' },
                                    { min: 8, message: 'Password must be at least 8 characters' }
                                ]}
                                extra="Password must be at least 8 characters"
                            >
                                <Input.Password />
                            </Form.Item>

                            <Form.Item
                                name="password_confirmation"
                                label="Confirm Password"
                                dependencies={['password']}
                                rules={[
                                    { required: true, message: 'Please confirm password' },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value || getFieldValue('password') === value) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(new Error('The two passwords do not match'));
                                        },
                                    }),
                                ]}
                            >
                                <Input.Password />
                            </Form.Item>
                        </>
                    ) : (
                        <>
                            <Form.Item>
                                <Button 
                                    type="link" 
                                    icon={<LockOutlined />}
                                    onClick={() => setShowPasswordField(!showPasswordField)}
                                >
                                    {showPasswordField ? 'Hide Password Change' : 'Change Password'}
                                </Button>
                            </Form.Item>
                            
                            {showPasswordField && (
                                <>
                                    <Form.Item
                                        name="password"
                                        label="New Password"
                                        rules={[
                                            { required: true, message: 'Please enter new password' },
                                            { min: 8, message: 'Password must be at least 8 characters' }
                                        ]}
                                        extra="Password must be at least 8 characters"
                                    >
                                        <Input.Password placeholder="Enter new password" />
                                    </Form.Item>

                                    <Form.Item
                                        name="password_confirmation"
                                        label="Confirm New Password"
                                        dependencies={['password']}
                                        rules={[
                                            { required: true, message: 'Please confirm new password' },
                                            ({ getFieldValue }) => ({
                                                validator(_, value) {
                                                    if (!value || getFieldValue('password') === value) {
                                                        return Promise.resolve();
                                                    }
                                                    return Promise.reject(new Error('The two passwords do not match'));
                                                },
                                            }),
                                        ]}
                                    >
                                        <Input.Password placeholder="Confirm new password" />
                                    </Form.Item>
                                </>
                            )}
                        </>
                    )}

                    <Form.Item>
                        <Space>
                            <Button 
                                type="primary" 
                                htmlType="submit"
                                loading={submitting}
                            >
                                {editingUser ? 'Update' : 'Create'}
                            </Button>
                            <Button 
                                onClick={() => {
                                    setModalVisible(false);
                                    setShowPasswordField(false);
                                    setSubmitting(false);
                                }}
                                disabled={submitting}
                            >
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </DashboardLayout>
    );
};

export default Users;
